import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Button Component
 * 
 * Variants: primary, secondary, danger, ghost
 * Sizes: sm, md, lg
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon: Icon = null,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 600,
    borderRadius: '8px',
    border: 'none',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all 150ms ease-out',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  };

  const sizeStyles = {
    sm: {
      height: '36px',
      padding: '0 16px',
      fontSize: '13px',
    },
    md: {
      height: '44px',
      padding: '0 24px',
      fontSize: '14px',
    },
    lg: {
      height: '52px',
      padding: '0 32px',
      fontSize: '15px',
    },
  };

  const variantStyles = {
    primary: {
      backgroundColor: '#E53935',
      color: '#FFFFFF',
    },
    secondary: {
      backgroundColor: '#FFFFFF',
      color: '#333333',
      border: '1px solid #E0E0E0',
    },
    danger: {
      backgroundColor: '#FFFFFF',
      color: '#F44336',
      border: '1px solid #F44336',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: '#666666',
    },
  };

  const hoverStyles = {
    primary: {
      ':hover': {
        backgroundColor: '#D32F2F',
        transform: 'translateY(-1px)',
      },
    },
    secondary: {
      ':hover': {
        backgroundColor: '#F5F5F5',
        borderColor: '#CCCCCC',
      },
    },
    danger: {
      ':hover': {
        backgroundColor: '#FFEBEE',
      },
    },
    ghost: {
      ':hover': {
        backgroundColor: '#F5F5F5',
        color: '#333333',
      },
    },
  };

  const disabledStyles = {
    opacity: 0.5,
    transform: 'none',
    pointerEvents: 'none',
  };

  const buttonStyle = {
    ...baseStyles,
    ...sizeStyles[size],
    ...variantStyles[variant],
    width: fullWidth ? '100%' : 'auto',
    ...(disabled || loading ? disabledStyles : {}),
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`btn btn-${variant} btn-${size} ${className}`}
      style={buttonStyle}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          const hover = hoverStyles[variant];
          if (hover && hover[':hover']) {
            Object.assign(e.currentTarget.style, hover[':hover']);
          }
        }
      }}
      onMouseLeave={(e) => {
        Object.assign(e.currentTarget.style, variantStyles[variant]);
      }}
      {...props}
    >
      {loading && (
        <Loader2 size={size === 'sm' ? 14 : 18} className="animate-spin" />
      )}
      {!loading && Icon && iconPosition === 'left' && (
        <Icon size={size === 'sm' ? 14 : 18} />
      )}
      {children}
      {!loading && Icon && iconPosition === 'right' && (
        <Icon size={size === 'sm' ? 14 : 18} />
      )}
    </button>
  );
};

export default Button;
